// notificationsContext.tsx
import { TrackedModalProvider } from "lib/analytics/react/modal";
import React, { createContext, useContext, ReactNode, useReducer } from "react";

type InterestToastStateType = {
  interestToastVisible: boolean;
  interestErrorToastVisible: boolean;
  existInterest: boolean;
  alarmModal?: boolean;
  ITEM_CD_DL?: string | string[] | undefined;
  fail?: (reason?: string) => void;
  success?: () => void;
  triggerSectionId?: string;
};

export type ActionType =
  | {
      type: "SET_INTEREST_TOAST_VISIBLE";
      payload: { toast: boolean; existInterest: boolean; alarmModal?: boolean };
    }
  | {
      type: "SET_INTEREST_ERROR_TOAST_VISIBLE";
      payload: { toast: boolean; existInterest: boolean };
    }
  | {
      type: "SET_ALARM_MODAL_VISIBLE";
      payload: {
        alarmModal: boolean;
        ITEM_CD_DL: string | string[] | undefined;
        fail?: (reason?: string) => void;
        success?: () => void;
        triggerSectionId?: string;
      };
    };

const InterestToastContext = createContext<InterestToastStateType | undefined>(
  undefined
);
const InterestToastDispatchContext = createContext<
  React.Dispatch<ActionType> | undefined
>(undefined);

const initialState: InterestToastStateType = {
  interestToastVisible: false,
  interestErrorToastVisible: false,
  existInterest: false,
  alarmModal: false,
  ITEM_CD_DL: undefined,
  triggerSectionId: undefined,
  success: () => {},
  fail: () => {},
};

export const InterestToastProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [value, dispatch] = useReducer(
    (state: InterestToastStateType, action: ActionType) => {
      switch (action.type) {
        case "SET_INTEREST_TOAST_VISIBLE":
          return {
            ...state,
            interestToastVisible: action.payload.toast,
            existInterest: action.payload.existInterest,
            alarmModal: action.payload?.alarmModal || false,
          };
        case "SET_INTEREST_ERROR_TOAST_VISIBLE":
          return {
            ...state,
            interestErrorToastVisible: action.payload.toast,
            existInterest: action.payload.existInterest,
          };
        case "SET_ALARM_MODAL_VISIBLE":
          return {
            ...state,
            alarmModal: action.payload.alarmModal,
            ITEM_CD_DL: action.payload.ITEM_CD_DL,
            triggerSectionId: action.payload?.triggerSectionId,
            fail: action.payload.fail,
            success: action.payload.success,
          };
        default:
          return state;
      }
    },
    initialState
  );

  return (
    <InterestToastContext.Provider value={value}>
      <InterestToastDispatchContext.Provider value={dispatch}>
        {children}
      </InterestToastDispatchContext.Provider>
    </InterestToastContext.Provider>
  );
};

export function useInterestToastState() {
  const state = useContext(InterestToastContext);
  if (!state) throw new Error("Cannot find InterestToastProvider");
  return state;
}

export function useInterestToastDispatch() {
  const dispatch = useContext(InterestToastDispatchContext);
  if (!dispatch) throw new Error("Cannot find InterestToastProvider");
  return dispatch;
}

/**
 * GlobalRiskAlarmNotiModal을 TrackedModalProvider로 래핑하는 컴포넌트
 * InterestToastContext의 alarmModal 값을 isOpen으로 전달
 */
export function TrackedGlobalAlarmModal({ children }: { children: ReactNode }) {
  const state = useInterestToastState();

  return (
    <TrackedModalProvider
      modalId="global_alarm_modal"
      isOpen={state.alarmModal || false}
      triggerSectionId={state.triggerSectionId || "global_alarm"}
      triggerType="auto"
    >
      {children}
    </TrackedModalProvider>
  );
}
